import { useEffect } from "react";
import { useAuth } from "./context/AuthContext";
import { PROXY } from "./lib/utils";
import { startPresenceHeartbeat } from "./lib/presenceHeartbeat";
import Faq from "./pages/Faq";
import Home from "./pages/Home";
import About from "./pages/About";
import Profile from "./pages/Profile";
import Courses from "./pages/Courses";
import Login from "./pages/auth/Login";
import Category from "./pages/Category";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import ContactUs from "./pages/ContactUs";
import Layout from "./components/layout/Layout";
import { Routes, Route, Navigate } from "react-router-dom";
import FilterCourses from "./pages/FilterCourses";
import CurrentCourse from "./pages/CurrentCourse";
import PrivateRoute from "./components/PrivateRoute";
import TeacherRoute from "./components/TeacherRoute";
import SingleCourse from "./components/SingleCourse";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import i18n from "i18next"; // Import i18n
import { applyDocumentLanguage, DEFAULT_LANGUAGE } from "./i18n/config";
import { pdfjs } from "react-pdf";
import GoogleOneTap from "./components/GoogleOneTap";
import Guidance from "./pages/Guidance";
import WhatsAppGroupButton from "./components/WhatsAppGroupButton";
import CourseLessonErrorBoundary from "./components/CourseLessonErrorBoundary";
import TeacherCourses from "./pages/TeacherCourses";
import TeacherCourseForm from "./pages/TeacherCourseForm";
import TeacherCourseContent from "./pages/TeacherCourseContent";
import CertificateVerification from "./pages/CertificateVerification";
import VerifyEmail from "./pages/auth/VerifyEmail";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

function App() {
  const { authState } = useAuth();
  const accessToken = authState.accessToken;
  useEffect(
    () =>
      startPresenceHeartbeat(() =>
        fetch(`${PROXY}/presence/heartbeat`, {
          method: "POST",
          credentials: "include",
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {},
        }),
      ),
    [accessToken],
  );
  useEffect(() => {
    const selectedLanguage = localStorage.getItem("lang") || DEFAULT_LANGUAGE;
    localStorage.setItem("lang", selectedLanguage);
    i18n.changeLanguage(selectedLanguage);
    applyDocumentLanguage(selectedLanguage);
  }, []);

  return (
    <>
      {/* ✅ Google One-Tap runs globally */}
      <GoogleOneTap />
      {/* <WhatsAppGroupButton /> */}

      {/* ✅ Your existing routing stays the same */}

      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="courses/:id" element={<SingleCourse />} />
          <Route
            path="certificates/verify"
            element={<CertificateVerification />}
          />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route element={<PrivateRoute />}>
            <Route
              path="courses/:id/current"
              element={
                <CourseLessonErrorBoundary>
                  <CurrentCourse />
                </CourseLessonErrorBoundary>
              }
            />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route element={<TeacherRoute />}>
            <Route path="teacher/courses" element={<TeacherCourses />} />
            <Route path="teacher/courses/new" element={<TeacherCourseForm />} />
            <Route path="teacher/courses/:id" element={<TeacherCourseForm />} />
            <Route
              path="teacher/courses/:id/content"
              element={<TeacherCourseContent />}
            />
          </Route>
          <Route path="category/:id" element={<Category />} />
          <Route path="courses/filter" element={<FilterCourses />} />
          <Route path="courses" element={<Courses />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="attachments" element={<Guidance />} />
          <Route
            path="guidance"
            element={<Navigate to="/attachments" replace />}
          />
          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="sign-up" element={<Register />} />
          <Route path="login" element={<Login />} />
          <Route path="forgot-pwd" element={<ForgotPassword />} />
          <Route path="reset-pwd" element={<ResetPassword />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
